import { Close, Save } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Fade, Grid, List, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, Fragment, useEffect, useState } from 'react';
// Components
import { Button as SMButton, Loader, Modal, Toast } from '../../../components';
import { getAdminRoleCategories, updateAdminRoleParts } from '../../../fetch';
import { IRoleCategory } from '../../../models';
import { CategoryListItem } from '../manage-roles-detail/CategoryListItem';

interface IManagePartsModal {
  open: boolean;
  onClose: () => void;
  roles: Array<any>;
}

export const ManagePartsModal: FC<IManagePartsModal> = ({ open, onClose, roles }) => {
  const handleManagePartsClose = () => {
    // reset state
    onClose();
    setRoleAssignments(null);
    setRoleCategories(null);
    setPartIds([]);
  };

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [roleCategories, setRoleCategories] = useState<IRoleCategory[] | null>(null);
  const [isLoadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [partIds, setPartIds] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState(null);

  const classes = useStyles();

  const fetchRoleCategories = async () => {
    try {
      setLoadingCategories(true);
      if (roles) {
        const res = await getAdminRoleCategories(roles.map(role => role.placeOrderRoleId));
        setRoleCategories(res.hierarchy);
        setRoleAssignments(res.roleAssignments);
      }
    } catch (error) {
      console.log(error);
      setErrorMessage('Error loading categories, please refresh and try again.');
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRoleCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Modal open={open} onClose={handleManagePartsClose} maxWidth="md">
        <Fade in={open}>
          <div>
            <Grid container spacing={1} direction="column">
              <Grid item>
                <Typography variant="h5">
                  Manage parts for {roles.map(role => role.name.trim()).join(', ')}
                </Typography>
              </Grid>
              {!isLoadingCategories && (
                <Grid className={classes.roleItem} item xs={12}>
                  <Typography variant="h6">Select Assignments</Typography>
                  <List
                    component="div"
                    aria-labelledby="nested-list-subheader"
                    className={classes.list}
                  >
                    {partIds &&
                    roleCategories &&
                    roleCategories.length > 0 &&
                    roleAssignments &&
                    roleAssignments.length === roles.length ? (
                      roleCategories.map((cat, index) => {
                        return (
                          <Fragment key={`CategoryListItem${index}`}>
                            <CategoryListItem
                              category={cat}
                              index={index}
                              roleCategories={roleCategories}
                              setRoleCategories={setRoleCategories}
                              setPartIds={setPartIds}
                              partIds={partIds}
                              roleAssignments={roleAssignments}
                              setRoleAssignments={setRoleAssignments}
                              roles={roles}
                            />
                          </Fragment>
                        );
                      })
                    ) : (
                      <Loader position="centered" />
                    )}
                  </List>
                </Grid>
              )}
              {isLoadingCategories && (
                <Box marginTop="8px">
                  <Loader position="centered" />
                </Box>
              )}
            </Grid>
          </div>
        </Fade>
        <Grid container alignItems="center" className={classes.saveWrapper}>
          <ButtonGroup>
            <SMButton
              id="save"
              color="primary"
              variant="contained"
              disabled={false}
              loading={isSaving ? 'Saving...' : ''}
              startIcon={<Save />}
              onClick={async () => {
                setSaving(true);
                try {
                  const roleAssignmentsToSend = [];
                  for (const role of roles) {
                    roleAssignmentsToSend.push({
                      placeOrderRoleId: role.placeOrderRoleId,
                      partIds: roleAssignments
                        .filter(assignment => role.placeOrderRoleId === assignment.placeOrderRoleId)
                        .flatMap(assignment => assignment.approvedPartIds)
                    });
                  }
                  await updateAdminRoleParts({
                    roleAssignments: roleAssignmentsToSend
                  });

                  setSuccessMessage('Role Updated!');
                } catch (error) {
                  setErrorMessage('Error updating role.');
                } finally {
                  setSaving(false);
                  handleManagePartsClose();
                }
              }}
            >
              Save
            </SMButton>
            <Button
              id="remove"
              variant="contained"
              color="inherit"
              startIcon={<Close />}
              onClick={() => {
                handleManagePartsClose();
              }}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Grid>
      </Modal>
      <Toast
        id="error"
        autoHideDuration={5000}
        message={errorMessage}
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        variant="error"
      />
      <Toast
        id="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
        variant="success"
      />
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  content: {
    marginTop: theme.spacing(0.5)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  roleContainer: {
    marginBottom: theme.spacing(1)
  },
  roleItem: {},
  roleItemSelectCategory: {
    paddingTop: `0 !important`
  },
  addCategoryButton: {},
  inputLabelCategory: {
    top: 0,
    left: 0,
    position: 'absolute'
  },
  addButton: {
    flex: 1
  },
  validationMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  list: {
    maxHeight: `30rem`,
    overflow: 'auto',
    margin: theme.spacing(1, 0)
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5)
  }
}));
